import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Pencil,
  Link as LinkIcon,
  Star,
  ChevronRight,
  ArrowRightLeft,
  Copy,
  Archive,
  Trash2,
  Share2,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { useFavoriteDocuments } from '../lib/space-prefs'
import { DocumentMoveDialog } from './DocumentMoveDialog'
import { fullDocument } from '../lib/types'
import { copyText } from '../lib/clipboard'

const TYPE_LABEL = {
  doc: 'Doc',
  spreadsheet: 'Spreadsheet',
  whiteboard: 'Whiteboard',
}

const renameSchema = z.object({
  title: z
    .string({ message: 'Title is required.' })
    .trim()
    .min(1, 'Title is required.')
    .min(2, 'Title must be at least 2 characters.')
    .max(200, 'Title must be at most 200 characters.'),
})

export function DocumentRowActions({ document: doc, spaceId, workspaceId }) {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  const favorites = useFavoriteDocuments(userId)
  const isFavorite = favorites.has(doc.id)
  const [open, setOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [coords, setCoords] = useState(null)

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const r = triggerRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.top, left: r.right + 4 })
  }, [open])
  useLayoutEffect(() => {
    if (!open || !coords) return
    const menu = menuRef.current
    const r = triggerRef.current?.getBoundingClientRect()
    if (!menu || !r) return
    const margin = 8
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    let top = r.top
    if (top + menu.offsetHeight > viewportH - margin) {
      top = Math.max(margin, viewportH - margin - menu.offsetHeight)
    }
    // Default opens to the RIGHT of the trigger; flip to the LEFT when that
    // would overflow the viewport (trigger near the right edge on mobile),
    // then clamp so it never runs off either edge.
    let left = r.right + 4
    if (left + menu.offsetWidth > viewportW - margin) {
      left = r.left - menu.offsetWidth - 4
    }
    left = Math.max(margin, Math.min(left, viewportW - menu.offsetWidth - margin))
    if (Math.abs(top - coords.top) > 1 || Math.abs(left - coords.left) > 1) {
      setCoords({ top, left })
    }
  }, [open, coords])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const renameForm = useForm({
    resolver: zodResolver(renameSchema),
    defaultValues: { title: doc.title },
  })
  useEffect(() => {
    if (renameOpen) renameForm.reset({ title: doc.title })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameOpen, doc.title])

  const renameMutation = useMutation({
    mutationFn: async (v) => {
      await api.patch(`/documents/${doc.id}`, { title: v.title.trim() })
    },
    onSuccess: () => {
      toast.success(`${TYPE_LABEL[doc.type]} renamed successfully.`)
      setRenameOpen(false)
      void qc.invalidateQueries({ queryKey: ['documents', spaceId] })
      void qc.invalidateQueries({ queryKey: ['document', doc.id] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.title) renameForm.setError('title', { message: fields.title })
      else toast.error(message)
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const src = await api.get(`/documents/${doc.id}`)
      const parsed = fullDocument.parse(src.data)
      const res = await api.post(`/spaces/${spaceId}/documents`, {
        type: parsed.type,
        title: `${parsed.title} (copy)`.slice(0, 200),
        body: parsed.body ?? undefined,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success(`${TYPE_LABEL[doc.type]} duplicated successfully.`)
      void qc.invalidateQueries({ queryKey: ['documents', spaceId] })
    },
    onError: (err) => {
      // Duplicating creates a new Document, so it shares the maxDocuments
      // cap. The global API interceptor already shows the single amber
      // "upgrade your plan" warning, so suppress our own error toast.
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/spaces/${spaceId}/documents/${doc.id}`)
    },
    onSuccess: () => {
      toast.success(`${TYPE_LABEL[doc.type]} deleted successfully.`)
      setConfirmDelete(false)
      favorites.remove(doc.id)
      void qc.invalidateQueries({ queryKey: ['documents', spaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const archiveMutation = useMutation({
    mutationFn: async (next) => {
      await api.patch(`/documents/${doc.id}`, { isArchived: next })
      return next
    },
    onSuccess: (next) => {
      toast.success(
        next
          ? `${TYPE_LABEL[doc.type]} archived successfully.`
          : `${TYPE_LABEL[doc.type]} restored from Archive.`,
      )
      void qc.invalidateQueries({ queryKey: ['documents', spaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function comingSoon(label) {
    setOpen(false)
    toast.info(`${label} — coming soon.`)
  }

  async function copyLink() {
    const url = `${window.location.origin}/app/documents/${doc.id}`
    // No transient "Copied" label here: this is a menu item and the menu
    // closes on click, so the toast IS the confirmation. Routed through the
    // shared helper for the execCommand fallback. See lib/clipboard.js.
    setOpen(false)
    if (!(await copyText(url))) {
      toast.error('Could not copy link.')
      return
    }
    toast.success('Link copied.')
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        aria-label={`${TYPE_LABEL[doc.type]} actions`}
        title={`${TYPE_LABEL[doc.type]} actions`}
        className="p-1 rounded text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-2)] cursor-pointer"
      >
        <MoreHorizontal size={14} />
      </button>

      {open && coords
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
              }}
            >
              <Menu
                ref={menuRef}
                title={`${TYPE_LABEL[doc.type]} settings`}
                isFavorite={isFavorite}
                onRename={() => {
                  setOpen(false)
                  setRenameOpen(true)
                }}
                onCopyLink={copyLink}
                onFavorite={() => {
                  favorites.toggle(doc.id)
                  toast.success(isFavorite ? 'Removed from Favourites.' : 'Added to Favourites.')
                  setOpen(false)
                }}
                onMoveTo={() => {
                  setOpen(false)
                  setMoveOpen(true)
                }}
                onDuplicate={() => {
                  setOpen(false)
                  duplicateMutation.mutate()
                }}
                onArchive={() => {
                  setOpen(false)
                  archiveMutation.mutate(!doc.isArchived)
                }}
                isArchived={Boolean(doc.isArchived)}
                onDelete={() => {
                  setOpen(false)
                  setConfirmDelete(true)
                }}
                onShare={() => comingSoon('Sharing and Permissions')}
              />
            </div>,
            document.body,
          )
        : null}

      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title={`Rename ${TYPE_LABEL[doc.type]}`}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRenameOpen(false)}
              disabled={renameMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={renameForm.handleSubmit((v) => renameMutation.mutate(v))}
              loading={renameMutation.isPending}
            >
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={renameForm.handleSubmit((v) => renameMutation.mutate(v))}>
          <TextField
            label="Title"
            required
            placeholder="Enter a Title"
            maxLength={200}
            error={renameForm.formState.errors.title?.message}
            {...renameForm.register('title')}
          />
        </form>
      </Modal>

      <DocumentMoveDialog
        open={moveOpen}
        document={doc}
        workspaceId={workspaceId}
        onClose={() => setMoveOpen(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${TYPE_LABEL[doc.type]}`}
        message={`Are you sure you want to delete "${doc.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  )
}

const Menu = forwardRef(function Menu(props, ref) {
  return (
    <div
      ref={ref}
      role="menu"
      className="w-64 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
    >
      <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        {props.title}
      </p>
      <Group>
        <Item icon={Pencil} label="Rename" onClick={props.onRename} />
        <Item icon={LinkIcon} label="Copy link" onClick={props.onCopyLink} />
      </Group>
      <Divider />
      <Group>
        <Item
          icon={Star}
          label={props.isFavorite ? 'Remove from Favourites' : 'Favourite'}
          onClick={props.onFavorite}
        />
        <Item icon={ArrowRightLeft} label="Move to" onClick={props.onMoveTo} />
        <Item icon={Copy} label="Duplicate" onClick={props.onDuplicate} />
        <Item
          icon={Archive}
          label={props.isArchived ? 'Unarchive' : 'Archive'}
          onClick={props.onArchive}
        />
        <Item icon={Trash2} label="Delete" danger onClick={props.onDelete} />
      </Group>
      <div className="px-2 py-2 bg-[color:var(--color-canvas)]">
        <button
          type="button"
          onClick={props.onShare}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-[color:var(--color-ink-strong)] bg-[color:var(--color-card)] border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Share2 size={14} />
          Sharing and Permissions
        </button>
      </div>
    </div>
  )
})

function Group({ children }) {
  return <div className="py-1">{children}</div>
}
function Divider() {
  return <div className="border-t border-[color:var(--color-rule)]" aria-hidden="true" />
}

function Item({ icon: Icon, label, hasSubmenu, danger, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[color:var(--color-card-hover)] cursor-pointer ' +
        (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink)]')
      }
    >
      <Icon
        size={14}
        className={
          'shrink-0 ' +
          (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-muted)]')
        }
      />
      <span className="flex-1 min-w-0 truncate font-medium">{label}</span>
      {hasSubmenu ? (
        <ChevronRight size={14} className="text-[color:var(--color-ink-muted)]" aria-hidden="true" />
      ) : null}
    </button>
  )
}
